import React, { useEffect, useState } from "react";
import { createGlobalStyle } from "styled-components";
import {
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Image,
  Link,
  Typography,
} from "@orion/ui";
import { useTranslation } from "react-i18next";
import { Divider } from "../components/Divider";
import ErrorMessage from "../components/ErrorMessage/ErrorMessage";
import { useCookies } from "react-cookie";
import { useLandmarkNavigation } from "../hooks";
import logoDefault from "../assets/images/logo.png";
import logoBper from "../assets/images/logo_Bper.png";
import { ACCESSIBILITY_URL, SECURITY_URL, Theme } from "../constants";
import { getEnvVar } from "@delorean/utils/env-utils";
import bperBg from "../assets/images/loginBackgroundBper.png";
import novaBg from "../assets/images/loginBackground.png";
import { SupportedLanguages } from "../assets/i18n";

/* const InputAutofillStyles = createGlobalStyle`
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active{
  -webkit-box-shadow: 0 0 0 30px #fafafa inset !important;
}`; */

type Props = { children: React.ReactNode };

export default function Layout({ children }: Props) {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(i18n.language);
  useLandmarkNavigation();
  const [cookies, setCookie] = useCookies(["locale"]);
  const changeLanguage = (lang: string) => {
    setCookie("locale", lang, { path: "/", secure: true });
    i18n.changeLanguage(lang);
    setSelectedLang(lang);
    setIsOpen(false);
  };

  const theme =
    localStorage.getItem("theme") || sessionStorage.getItem("theme");
  const logoSrc = theme === Theme.BPER ? logoBper : logoDefault;
  const logoWidth = theme === Theme.BPER ? 125.83 : 130;
  const logoHeight = theme === Theme.BPER ? 30.98 : 52;
  const bg = theme === Theme.BPER ? bperBg : novaBg;

  useEffect(() => {
    // Application must start always in Italian by default
    changeLanguage(SupportedLanguages.IT);
  }, []);

  return (
    <Box
      flex
      style={{
        height: "100vh",
      }}
    >
      <Box
        flex
        column
        justifyBetween
        style={{
          maxWidth: "828px",
          width: "100%",
          margin: "0 auto",
        }}
      >
        <Box
          flex
          column
          justifyBetween
          style={{
            width: "576px",
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* L1 - Header */}
          <Box flex column>
            <Box mt12>
              <Image
                role="banner"
                aria-label={t("accessibility.logo")}
                alt={t("accessibility.logo")}
                placeholder="LDHnjdv.3VTVt,slR;I[_4aMO8Xg"
                src={logoSrc}
                width={logoWidth}
                height={logoHeight}
              />
            </Box>
            <Box style={{ width: "100%", marginTop: "29px" }}>
              <ErrorMessage />
            </Box>
          </Box>

          {/* L2 - Main Content */}
          <Box flex justifyCenter>
            {children}
          </Box>

          {/* L3 Footer */}
          <Box
            flex
            justifyEnd
            alignItems="center"
            mb9
            role="footer"
            data-landmark="2"
            style={{
              height: "22px",
            }}
          >
            <Box
              flex
              alignItems="center"
              style={{
                cursor: "pointer",
              }}
            >
              <Link
                color="primary100"
                type="button"
                onClick={() => window.open(SECURITY_URL, "_blank")}
                role="link"
                aria-label={t("login.security")}
              >
                {t("login.security")}
              </Link>
              <Divider />
              <Link
                color="primary100"
                type="button"
                onClick={() => window.open(ACCESSIBILITY_URL, "_blank")}
                role="link"
                aria-label={t("login.accessibility")}
              >
                {t("login.accessibility")}
              </Link>
              <Divider />
              <DropdownMenu onOpenChange={setIsOpen}>
                <DropdownMenuTrigger
                  asChild
                  tabIndex={0}
                  role="button"
                  aria-haspopup="menu"
                  aria-expanded={isOpen}
                  aria-controls="language-menu"
                  aria-label={t("accessibility.lang")}
                  onClick={() => setIsOpen((prev) => !prev)}
                >
                  <Typography
                    textPrimary100
                    fontMedium
                    style={{
                      textDecoration: "underline",
                      cursor: "pointer",
                      textUnderlineOffset: " 2px",
                    }}
                  >
                    <span aria-hidden="true">
                      {t("login.language.language")}
                    </span>
                  </Typography>
                </DropdownMenuTrigger>
                <DropdownMenuContent id="language-menu" role="menu">
                  <DropdownMenuItem
                    justifyCenter
                    p9
                    onClick={() => changeLanguage(SupportedLanguages.EN)}
                    role="menuitem"
                  >
                    <Typography style={{ cursor: "pointer" }}>
                      {t("login.language.en")}
                    </Typography>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    justifyCenter
                    p9
                    onClick={() => changeLanguage(SupportedLanguages.IT)}
                    role="menuitem"
                  >
                    <Typography style={{ cursor: "pointer" }}>
                      {t("login.language.it")}
                    </Typography>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Advertising section */}
      <Box flex width="5/12" position="relative" aria-hidden>
        <Image
          tabIndex={-1}
          style={{
            minHeight: "560px",
          }}
          aria-hidden
          alt={t("accessibility.advertising")}
          fill
          fit="cover"
          placeholder="L39Q5H0%9Z~BXn~CkCM|~Cx]jZae"
          src={bg}
        />
      </Box>
    </Box>
  );
}
